import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { companyMetrics, whyErbity } from '../mock';

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#FAF7F5]">
      <HeroBanner
        image="/hero/about.jpg"
        overlay="default"
        height="min-h-[480px] lg:min-h-[560px]"
      >
        <div className="pt-[120px] lg:pt-[140px] pb-14 lg:pb-16">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-end erbity-fade-up">
              <div className="lg:col-span-8">
                <EyebrowLabel>About Erbity</EyebrowLabel>
                <SectionTitle as="h1" className="mt-6 text-[44px] lg:text-[70px] leading-[1.04]">
                  Founded by engineers.<br />
                  Built for <span className="italic text-[#7E2E4A]">semiconductor</span> teams.
                </SectionTitle>
              </div>
              <div className="lg:col-span-4 erbity-fade-up-1">
                <p className="text-[16px] text-[#3B333A] leading-relaxed">
                  Erbity was founded on a simple premise: the semiconductor industry
                  is too specialised to be served by generalist recruiters. We staff
                  the way we build silicon &mdash; precisely, patiently, and with domain depth.
                </p>
              </div>
            </div>
          </Container>
        </div>
      </HeroBanner>

      <section className="pb-24">
        <Container>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#EADDE2] border border-[#EADDE2] rounded-2xl overflow-hidden">
            {companyMetrics.secondary.map((m) => (
              <div key={m.label} className="bg-white p-6 lg:p-8">
                <div className="font-serif text-[30px] lg:text-[38px] leading-none text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>{m.value}</div>
                <div className="mt-2 text-[13px] text-[#4E434A]">{m.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 bg-white border-y border-[#EADDE2]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <EyebrowLabel>Our philosophy</EyebrowLabel>
              <SectionTitle className="mt-5 text-[32px] lg:text-[44px] leading-[1.05]">
                Talent is an engineering problem.
              </SectionTitle>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[16px] text-[#4E434A] leading-relaxed">
              <p>
                Every hiring cycle we run starts the way an ASIC program does &mdash;
                with a specification. We interview your architects, understand
                the technology and the culture, and translate that into a search
                brief that our engineering-led recruiters can act on.
              </p>
              <p>
                Our practice covers the full silicon stack: RTL and verification,
                DFT, physical design, analog and mixed-signal, embedded software,
                and post-silicon validation. We recruit exclusively in these
                disciplines &mdash; nothing else.
              </p>
              <p>
                We are headquartered in Bengaluru with active talent networks in
                Hyderabad, Pune and Chennai &mdash; the four cities where most of
                India&rsquo;s chip design engineers live and work.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-14 lg:py-16 bg-[#FAF7F5]">
        <Container>
          <div className="max-w-2xl mb-10">
            <EyebrowLabel>Principles</EyebrowLabel>
            <SectionTitle className="mt-5 text-[32px] lg:text-[44px]">Six commitments we hold ourselves to.</SectionTitle>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyErbity.map((w, i) => (
              <div key={w.title} className="bg-white rounded-2xl border border-[#EADDE2] p-7">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">
                  <span className="w-6 h-px bg-[#C4506F]" /> 0{i + 1}
                </div>
                <h3 className="mt-4 font-serif text-[22px] text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{w.title}</h3>
                <p className="mt-3 text-[14px] text-[#4E434A] leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex items-center justify-center">
            <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#1A1420] text-white text-[15px] font-medium pl-6 pr-2 py-2 rounded-full hover:bg-[#3D1A2A] transition-colors">
              <span className="py-1">Partner with Erbity</span>
              <span className="w-10 h-10 rounded-full bg-[#C4506F] flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight size={16} />
              </span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
